import { Controller, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Field, FieldGroup, FieldLabel, FieldError } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

import { FilterSelect, SimpleSelect } from '@/shared/ui';
import { LocationAddressFilterType } from '@/entities/location';

import { useServiceIdentification, getServiceTypeOptions, type ConsignmentFormValues } from '../model';

interface ServiceIdentificationCardProps {
  form: UseFormReturn<ConsignmentFormValues>;
}

export function ServiceIdentificationCard({ form }: ServiceIdentificationCardProps) {
  const { t } = useTranslation();

  const {
    control,
    register,
    watch,
    setValue,
    formState: { errors }
  } = form;

  const serviceType = watch('serviceType');

  const { locationOptions, isLoading, resetSearch, setSearchValue } = useServiceIdentification({ serviceType });

  const serviceTypeOptions = useMemo(() => getServiceTypeOptions(t), [t]);

  const handleServiceTypeChange = useCallback(
    (value: ConsignmentFormValues['serviceType']) => {
      setValue('serviceType', value);
      setValue('serviceDestination', '');
      resetSearch();
    },
    [resetSearch, setValue]
  );

  const showDurationField = serviceType === LocationAddressFilterType.STORAGE;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-medium text-muted-foreground">{t('request.serviceIdent.label')}</CardTitle>
      </CardHeader>
      <CardContent>
        <FieldGroup>
          <Field>
            <Controller
              name="serviceType"
              control={control}
              render={({ field }) => (
                <FilterSelect<ConsignmentFormValues['serviceType']>
                  value={field.value}
                  onChange={handleServiceTypeChange}
                  options={serviceTypeOptions}
                  placeholder={t('validity.valueMissing.select')}
                  width="full"
                />
              )}
            />
            <FieldError errors={[errors.serviceType]} />
          </Field>

          <Field className="flex flex-col gap-2">
            <Label className="text-sm font-medium">{t('request.destination.label')}</Label>
            <Controller
              name="serviceDestination"
              control={control}
              render={({ field }) => (
                <SimpleSelect
                  options={locationOptions}
                  value={field.value}
                  onChange={field.onChange}
                  onInputValueChange={setSearchValue}
                  placeholder={isLoading ? t('common.loading') : t('gen.autocomplete.title')}
                  emptyMessage={t('dashboard.locations.empty')}
                  className="w-full"
                />
              )}
            />
            <FieldError errors={[errors.serviceDestination]} />
          </Field>

          {showDurationField && (
            <Field>
              <FieldLabel htmlFor="durationInDays">{t('request.storageDuration.label')}</FieldLabel>
              <Input id="durationInDays" type="number" {...register('durationInDays')} placeholder="0" min="0" />
              <FieldError errors={[errors.durationInDays]} />
            </Field>
          )}

          <Field>
            <FieldLabel htmlFor="serviceInstructions">{t('request.arrivalInstructions.label')}</FieldLabel>
            <Textarea
              id="serviceInstructions"
              {...register('serviceInstructions')}
              placeholder={t('request.arrivalInstructions.label')}
              rows={3}
            />
            <FieldError errors={[errors.serviceInstructions]} />
          </Field>
        </FieldGroup>
      </CardContent>
    </Card>
  );
}
